/**
 * `<TableWeb>` — une TABLE DE VALEURS (`CAP-09`, tranche T7), dans la console :
 * dans une fiche relue (illustration de genre `table`), dans l'énoncé d'un
 * essai, et CORRIGÉE dans « Travaux et corrections » — la table que l'élève a
 * remplie, case par case.
 *
 * Un vrai `<table>` : le nom de chaque ligne est son en-tête (`<th scope="row">`),
 * un lecteur d'écran la parcourt sans rien de plus. Une case corrigée ne se dit
 * jamais par sa seule couleur : elle porte un mot (« juste », « à revoir »,
 * « vide ») et, s'il le faut, la valeur attendue. Aucune couleur littérale :
 * chaque état est une classe de `theme.css`.
 */
import type { EtatDeCase, TableARemplirDto, TableCorrigeeDto, TableDeValeurs } from '@silex/shared';
import type { ReactNode } from 'react';

import { TexteMaths } from '../TexteMaths';

const MOT: Readonly<Record<EtatDeCase, string | null>> = {
  donnee: null,
  juste: 'juste',
  a_revoir: 'à revoir',
  vide: 'vide',
};

const riche = (ecrit: string): string => `\\(${ecrit.replace(/\\\(|\\\)|\$/g, '').trim()}\\)`;

const estCorrigee = (
  t: TableDeValeurs | TableARemplirDto | TableCorrigeeDto,
): t is TableCorrigeeDto => t.lignes.length > 0 && 'cases' in (t.lignes[0] as object);

export function TableWeb({
  table,
  decrit,
  decritVisible = false,
  testid = 'table-valeurs',
}: {
  readonly table: TableDeValeurs | TableARemplirDto | TableCorrigeeDto;
  /** Ce que la table montre (une fiche) ; il devient la légende du tableau. */
  readonly decrit?: string;
  /** Écrit la description sous la table, pour le relecteur d'une fiche. */
  readonly decritVisible?: boolean;
  readonly testid?: string;
}): ReactNode {
  const corrigee = estCorrigee(table);
  /* Une matrice (tranche T41) : sans en-tête de ligne, entre ses délimiteurs. */
  const matrice = 'matrice' in table ? table.matrice : undefined;
  const grille = (
    <table className="table-valeurs__table">
      {decrit !== undefined ? <caption className="sr-only">{decrit}</caption> : null}
      <tbody>
        {table.lignes.map((ligne, i) => {
          const cases = corrigee
            ? (ligne as TableCorrigeeDto['lignes'][number]).cases
            : (ligne as TableARemplirDto['lignes'][number]).valeurs.map((v) => ({
                valeur: v,
                etat: 'donnee' as EtatDeCase,
                attendu: undefined as string | undefined,
              }));
          return (
            <tr key={i}>
              {matrice === undefined ? (
                <th scope="row">
                  <TexteMaths texte={ligne.nom} />
                </th>
              ) : null}
              {cases.map((c, j) => (
                <td key={j} className={`table-valeurs__case table-valeurs__case--${c.etat}`}>
                  {c.valeur === null ? (
                    <span className="table-valeurs__vide" aria-hidden="true">
                      …
                    </span>
                  ) : (
                    <TexteMaths texte={riche(c.valeur)} />
                  )}
                  {MOT[c.etat] !== null ? (
                    <span className="table-valeurs__mot">{MOT[c.etat]}</span>
                  ) : null}
                  {c.attendu !== undefined ? (
                    <span className="table-valeurs__attendu">
                      attendu <TexteMaths texte={riche(c.attendu)} />
                    </span>
                  ) : null}
                </td>
              ))}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
  if (matrice !== undefined) {
    return (
      <figure className="fiche-dessin table-valeurs" data-testid={testid}>
        <div className="table-valeurs__defile">
          <span
            className={`table-valeurs__matrice table-valeurs__matrice--${matrice}`}
            data-testid={`${testid}-matrice`}
          >
            {grille}
          </span>
        </div>
      </figure>
    );
  }
  return (
    <figure className="fiche-dessin table-valeurs" data-testid={testid}>
      <div className="table-valeurs__defile">{grille}</div>
      {decritVisible && decrit !== undefined ? (
        <figcaption className="fiche-dessin__decrit">
          <span className="fiche-dessin__decrit-titre">
            Description lue à l’élève qui ne voit pas
          </span>
          {decrit}
        </figcaption>
      ) : null}
    </figure>
  );
}
